import { useEffect, useRef, useState } from "react";

/**
 * Smoothly interpolating counter for total registered users.
 *
 * On each 10-min refresh we snap to the actual BQ value and recalculate the
 * registration rate. Between refreshes, requestAnimationFrame increments the
 * displayed number at the observed rate so it never feels frozen.
 */
export function RegisteredCounter({
  total,
  ratePerMs,
}: {
  total: number;
  ratePerMs: number;
}) {
  const [display, setDisplay] = useState(total);
  const baseRef = useRef(total);
  const startRef = useRef(performance.now());

  // When a new total arrives (after BQ refresh), snap base + reset timer.
  useEffect(() => {
    baseRef.current = total;
    startRef.current = performance.now();
    setDisplay(total);
  }, [total]);

  // Smooth tick — runs continuously, driven by ratePerMs.
  useEffect(() => {
    if (ratePerMs <= 0) return;
    let raf: number;
    const tick = () => {
      const elapsed = performance.now() - startRef.current;
      setDisplay(baseRef.current + elapsed * ratePerMs);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [ratePerMs]);

  if (total === 0) return null;

  return (
    <div className="registered-counter">
      {Math.floor(display).toLocaleString("en-US")} REGISTERED USERS
    </div>
  );
}
